import {
  ApiConfig,
  DistributionRow,
  ProductSummary,
  RawPayload,
  Review,
  ReviewPage,
  ReviewSummary,
  SortOption,
  Tab,
  View,
} from './types';

/**
 * SmartCustomer's public v2 API, called straight from the visitor's browser.
 *
 * These are unauthenticated, CORS-open GETs — no key, no token, no cookie — which is
 * what lets this page paginate, sort, filter and search without a single endpoint of
 * our own behind it. Nothing here may ever gain a credential: anything secret enough to
 * need one is too secret to ship in a bundle.
 *
 * The normalizers below are the only reading of a SmartCustomer payload anywhere in the
 * app. The server fetches page one so its reviews are in the raw HTML, but it hands the
 * payload over exactly as it arrived and this turns it into cards — the same function
 * for the page the server fetched and every page the browser fetches after it, so a card
 * cannot change shape when a visitor clicks "next".
 */

/** One decimal, the way every rating on this page is shown. */
function round1(value: unknown): number {
  return Math.round(Number(value ?? 0) * 10) / 10;
}

/**
 * Formatted here rather than left to the browser's locale so that the date the server
 * drew and the date the browser draws are the same string. Every timestamp the API
 * returns is UTC, and reading it as UTC is what keeps a visitor in Sydney from seeing
 * every review dated a day later than the crawler did.
 */
function formatDate(value: string | null | undefined, style: 'label' | 'iso'): string | null {
  if (!value) {
    return null;
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return null;
  }

  if (style === 'iso') {
    return date.toISOString().slice(0, 10);
  }

  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

/**
 * A first name and a last initial, and nothing else. Only these two fields are ever read
 * off the author — v1 returned an email address here and v2 does not, and naming the two
 * we want is what keeps it that way if it ever comes back.
 */
function authorDisplay(author: any): string {
  const first = String(author?.first_name ?? '').trim();
  const last = String(author?.last_name ?? '').trim();

  if (!first && !last) {
    return 'Verified customer';
  }

  return `${first}${last ? ` ${last.charAt(0).toUpperCase()}.` : ''}`.trim();
}

function initials(author: string): string {
  const letters = author
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase())
    .join('');

  return letters.slice(0, 2) || '★';
}

function text(value: unknown): string | null {
  const trimmed = String(value ?? '').trim();

  return trimmed === '' ? null : trimmed;
}

/**
 * The review came from a confirmed purchase. `is_verified` is the platform's own word
 * for it on seller reviews; product reviews carry no such flag, so an order number
 * standing behind the review is the signal.
 */
function isVerified(review: any): boolean {
  return Boolean(review?.is_verified) || String(review?.order_id ?? '').trim() !== '';
}

function normalizeReview(review: any, tab: Tab): Review {
  const author = authorDisplay(tab === 'products' ? review?.user : review?.author);

  const normalized: Review = {
    id: Number(review?.id ?? 0),
    rating: Number(review?.rating ?? 0),
    title: text(review?.title),
    body: text(review?.content),
    author,
    initials: initials(author),
    verifiedBuyer: isVerified(review),
    helpfulVotes: Number(review?.num_positive_votes ?? 0),
    publishedAt: formatDate(review?.published, 'iso'),
    publishedLabel: formatDate(review?.published, 'label'),
    dateOfExperienceLabel: tab === 'products' ? null : formatDate(review?.experience_date, 'label'),
  };

  if (tab === 'products') {
    const product = review?.product ?? {};

    // Deliberately not spread: the payload carries the magazine's whole marketing
    // description, two kilobytes a card, and none of it is rendered.
    normalized.product = {
      title: text(product?.title),
      url: text(product?.product_link),
      image: text(product?.image_url),
      averageRating: round1(product?.average_rating),
      reviewCount: Number(product?.num_reviews ?? 0),
    };
  }

  return normalized;
}

/**
 * One page of reviews, however it arrived: the server hands over the payload it fetched
 * for page one, and fetchReviews below brings back the same shape for every page after
 * it. Both land here.
 */
export function normalizeReviewPage(payload: RawPayload, tab: Tab): ReviewPage {
  const reviews: any[] = payload?.data?.reviews ?? [];

  return {
    tab,
    total: Number(payload?.meta?.pagination?.count ?? 0),
    reviews: reviews.map((review) => normalizeReview(review, tab)),
  };
}

/**
 * The API spells its star buckets two ways, and which one you get depends on the
 * endpoint rather than on whether they are seller or product ratings: the summary
 * endpoint answers with a list of {value, total} for both of its blocks, while the
 * product-reviews endpoint answers with a {"5": n} map. Both are read into one shape,
 * five down to one, with any missing tier as a zero rather than a gap.
 */
function starBuckets(ratings: unknown): number[] {
  const counts: Record<number, number> = {};

  if (Array.isArray(ratings)) {
    ratings.forEach((entry: any) => {
      counts[Number(entry?.value ?? 0)] = Number(entry?.total ?? 0);
    });
  } else if (ratings && typeof ratings === 'object') {
    Object.entries(ratings as Record<string, unknown>).forEach(([stars, total]) => {
      counts[Number(stars)] = Number(total ?? 0);
    });
  }

  return [5, 4, 3, 2, 1].map((stars) => counts[stars] ?? 0);
}

/**
 * Bars are a share of the total, so an empty total has to short-circuit rather than
 * divide by it.
 */
function distributionRows(buckets: number[], reviewCount: number): DistributionRow[] {
  return buckets.map((count, index) => ({
    stars: 5 - index,
    count,
    percent: reviewCount > 0 ? Math.round((count / reviewCount) * 1000) / 10 : 0,
  }));
}

function averageOfBuckets(buckets: number[]): number {
  const total = buckets.reduce((sum, count) => sum + count, 0);

  if (total < 1) {
    return 0;
  }

  return round1(buckets.reduce((sum, count, index) => sum + (5 - index) * count, 0) / total);
}

/** Ratings phrased the way a shopper reads them. */
function ratingLabel(rating: number): string {
  const label = [
    [4.5, 'Excellent'],
    [3.5, 'Great'],
    [2.5, 'Average'],
    [1.5, 'Poor'],
  ].find(([floor]) => rating >= (floor as number));

  if (label) {
    return label[1] as string;
  }

  return rating > 0 ? 'Bad' : '';
}

/**
 * The headline figures, from the two calls that carry them.
 *
 * The count and the buckets are taken from the reviews call rather than the summary
 * call: the summary's own buckets answer for the narrower default set — 337 — while its
 * headline count is 469, and bars that do not add up to the number beside them are worse
 * than no bars. The average is the platform's own headline, which is what makes 4.5 from
 * 469 the same pair a shopper finds on SmartCustomer's profile.
 *
 * The fallbacks are for the half-outage, where one of the two calls answered and the
 * other did not: an average computed from real buckets beats printing "0.0" beside a
 * real count of 469.
 */
export function normalizeSummary(summaryPayload: RawPayload, sellerPayload: RawPayload): ReviewSummary {
  const buckets = starBuckets(sellerPayload?.data?.ratings);

  let reviewCount = Number(sellerPayload?.meta?.pagination?.count ?? 0);

  if (reviewCount < 1) {
    reviewCount = buckets.reduce((sum, count) => sum + count, 0);
  }

  let averageRating = round1(summaryPayload?.average_ratings?.rating);

  if (averageRating <= 0) {
    averageRating = averageOfBuckets(buckets);
  }

  return {
    averageRating,
    reviewCount,
    label: ratingLabel(averageRating),
    distribution: distributionRows(buckets, reviewCount),
  };
}

/**
 * The magazines' own figures, which arrive inside the same summary call. A different
 * shape from the seller summary on purpose, so nothing downstream can pass one where the
 * other belongs and quietly present magazine ratings as the company's.
 */
export function normalizeProductSummary(
  summaryPayload: RawPayload,
  productPayload: RawPayload
): ProductSummary {
  const block = summaryPayload?.review_numbers?.product_reviews ?? {};

  // The two endpoints disagree by a couple of reviews — the summary says 52,540 and the
  // list says 52,538 — so when the list has been fetched, that is the number to print:
  // it is the one the pager below it counts pages from.
  const reviewCount = Number(productPayload?.meta?.pagination?.count ?? block?.num_reviews ?? 0);

  return {
    averageRating: round1(block?.average_rating),
    reviewCount,
    distribution: distributionRows(starBuckets(block?.ratings), reviewCount),
  };
}

export function buildUrl(api: ApiConfig, view: View, sort: SortOption): string {
  const path = view.tab === 'products' ? 'product-reviews' : 'reviews';
  const url = new URL(`${api.baseUrl}/businesses/${api.business}/${path}`);

  url.searchParams.set('per_page', String(api.perPage));
  url.searchParams.set('page', String(view.page));
  url.searchParams.set('sorting[field]', sort.field);
  url.searchParams.set('sorting[direction]', sort.direction);

  // Seller counts depend on this flag and product counts have no such notion: the
  // default set is 337 reviews, `any` is the 469 the brand's public profile shows.
  if (view.tab === 'seller') {
    url.searchParams.set('recommended', api.recommended);
  }

  // `search`, not `q`. The documented v1 parameter is `q`, and v2 accepts it, ignores
  // it, and answers with the unfiltered set — which looks exactly like a search that
  // matched everything.
  if (view.search) {
    url.searchParams.set('search', view.search);
  }

  view.ratings.forEach((rating) => url.searchParams.append('ratings[]', String(rating)));

  return url.toString();
}

export async function fetchReviews(
  api: ApiConfig,
  view: View,
  sort: SortOption,
  signal?: AbortSignal
): Promise<ReviewPage> {
  const response = await fetch(buildUrl(api, view, sort), { signal });

  if (!response.ok) {
    throw new Error(`SmartCustomer answered ${response.status}`);
  }

  return normalizeReviewPage(await response.json(), view.tab);
}
